import React from 'react';
import { CodeBlock } from '@/components/docs/CodeBlock';

export default function CLIPage() {
  return (
    <article className="max-w-3xl" data-testid="docs-cli">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">CLI Reference</h1>
      <p className="text-base text-white/50 leading-relaxed mb-8">Generate skeleton bones from your running app.</p>

      <h2 className="text-xl font-bold tracking-tight mb-4">Basic usage</h2>
      <CodeBlock language="bash" code={`npx ghostlayout build`} />
      <p className="text-sm text-white/40 mt-3 mb-8 leading-relaxed">Auto-detects your dev server, captures at default breakpoints, outputs to <code className="text-white/60 bg-white/[0.06] px-1 py-0.5 rounded text-xs">./src/bones/</code>.</p>

      <h2 className="text-xl font-bold tracking-tight mb-4">Options</h2>
      <div className="border border-white/[0.06] rounded-lg overflow-hidden mb-8">
        <table className="w-full text-sm">
          <thead><tr className="bg-white/[0.03] text-left"><th className="px-4 py-3 font-mono text-xs text-white/40">Flag</th><th className="px-4 py-3 font-mono text-xs text-white/40">Default</th><th className="px-4 py-3 font-mono text-xs text-white/40">Description</th></tr></thead>
          <tbody className="text-white/60">
            <tr className="border-t border-white/[0.04]"><td className="px-4 py-3 font-mono text-xs">--url &lt;url&gt;</td><td className="px-4 py-3 text-xs">auto-detect</td><td className="px-4 py-3 text-xs">Dev server URL</td></tr>
            <tr className="border-t border-white/[0.04]"><td className="px-4 py-3 font-mono text-xs">--output &lt;path&gt;</td><td className="px-4 py-3 text-xs">./src/bones</td><td className="px-4 py-3 text-xs">Output directory</td></tr>
            <tr className="border-t border-white/[0.04]"><td className="px-4 py-3 font-mono text-xs">--breakpoints &lt;list&gt;</td><td className="px-4 py-3 text-xs">375,768,1024,1440</td><td className="px-4 py-3 text-xs">Comma-separated viewport widths</td></tr>
            <tr className="border-t border-white/[0.04]"><td className="px-4 py-3 font-mono text-xs">--wait &lt;ms&gt;</td><td className="px-4 py-3 text-xs">800</td><td className="px-4 py-3 text-xs">Wait time after page load</td></tr>
          </tbody>
        </table>
      </div>

      <h2 className="text-xl font-bold tracking-tight mb-4">Examples</h2>
      <CodeBlock language="bash" code={`# Custom URL
npx ghostlayout build --url http://localhost:3001

# Custom breakpoints
npx ghostlayout build --breakpoints 320,768,1920

# Custom output
npx ghostlayout build --output ./generated/skeletons

# All options
npx ghostlayout build --url http://localhost:3000 --breakpoints 375,768,1440 --wait 1000 --output ./src/bones`} />

      <h2 className="text-xl font-bold tracking-tight mb-4 mt-10">Auto-detect</h2>
      <p className="text-sm text-white/40 mb-4 leading-relaxed">If no <code className="text-white/60 bg-white/[0.06] px-1 py-0.5 rounded text-xs">--url</code> is passed, the CLI scans these ports in order:</p>
      <CodeBlock language="text" code={`3000, 5173, 5174, 4321, 8080, 8000, 4200, 3001, 3002`} />

      <h2 className="text-xl font-bold tracking-tight mb-4 mt-10">Sample output</h2>
      <CodeBlock language="bash" code={`  GhostLayout CLI v1.0.0

  Auto-detecting dev server...
  Found dev server at http://localhost:3000
  Connecting to: http://localhost:3000
  Breakpoints: 375px, 768px, 1024px, 1440px

  Browser launched
  Found 3 skeleton(s):
     - user-card
     - product-card
     - dashboard-stats

  Extracting: user-card
     Saved to user-card.bones.json
        375px: 6 bones
        768px: 6 bones
        1024px: 6 bones
        1440px: 6 bones

  Generated registry.js

  Done!`} />

      <h2 className="text-xl font-bold tracking-tight mb-4 mt-10">When to re-run</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="border border-white/[0.06] rounded-lg p-5 bg-[#0A0A0A]">
          <h4 className="font-bold text-sm text-emerald-400 mb-2">Re-run when</h4>
          <ul className="space-y-1 text-xs text-white/40">
            <li>- You add a new Skeleton component</li>
            <li>- You change the layout of a component</li>
            <li>- You change breakpoints</li>
          </ul>
        </div>
        <div className="border border-white/[0.06] rounded-lg p-5 bg-[#0A0A0A]">
          <h4 className="font-bold text-sm text-white/60 mb-2">Skip when</h4>
          <ul className="space-y-1 text-xs text-white/40">
            <li>- You change text content</li>
            <li>- You change colors or animations</li>
            <li>- You toggle loading state</li>
          </ul>
        </div>
      </div>
    </article>
  );
}
